import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-final-form';
import { DocTypes } from '@websky/graphql';
import { InputType } from '../../../../Passenger/types';
import { validate } from '../../../../Passenger/utils';
import { TravellerDocumentFields, TravellerFields } from '../../../types';
import { DocumentFields } from '../../../consts';
import { getAvailabilityDocumentExpireDate, getCitizenships, getDocumentTypes } from './utils';
export const useDocumentFields = (index, validators, traveller, primaryCountryCodes) => {
    var _a, _b, _c, _d;
    const { t: tPassenger } = useTranslation('Passenger');
    const form = useForm();
    const formState = useFormState();
    const selectedDocType = (_b = (_a = formState.values[TravellerFields.Documents]) === null || _a === void 0 ? void 0 : _a[index]) === null || _b === void 0 ? void 0 : _b[TravellerDocumentFields.DocType];
    const selectedCitizenship = (_d = (_c = formState.values[TravellerFields.Documents]) === null || _c === void 0 ? void 0 : _c[index]) === null || _d === void 0 ? void 0 : _d[TravellerDocumentFields.Citizenship];
    const citizenships = getCitizenships(traveller, primaryCountryCodes);
    const documentTypes = getDocumentTypes(selectedCitizenship, validators);
    const isDocExpireDateHidden = getAvailabilityDocumentExpireDate(selectedDocType, validators);
    const isBirthRegDocument = selectedDocType === DocTypes.BirthRegDocument;
    const documentFields = [...DocumentFields];
    const requiredValidate = (value) => (!value ? 'Required' : undefined);
    const citizenshipValidate = requiredValidate;
    const docTypeValidate = requiredValidate;
    const docNumberValidate = (value, allValues) => {
        var _a, _b, _c, _d;
        const validation = validators === null || validators === void 0 ? void 0 : validators.values.find(v => v.name === TravellerDocumentFields.DocNumber);
        return validate(value, Object.assign(Object.assign({}, ((_b = (_a = allValues[TravellerFields.Documents]) === null || _a === void 0 ? void 0 : _a[index]) !== null && _b !== void 0 ? _b : {})), { citizenships: (_d = (_c = formState.values[TravellerFields.Documents]) === null || _c === void 0 ? void 0 : _c[index]) === null || _d === void 0 ? void 0 : _d[TravellerDocumentFields.Citizenship] }), InputType.Text, validation === null || validation === void 0 ? void 0 : validation.validationRules);
    };
    const docExpireDateValidate = (value, allValues) => {
        var _a, _b, _c, _d;
        const validation = validators === null || validators === void 0 ? void 0 : validators.values.find(v => v.name === TravellerDocumentFields.DocExpiryDate);
        return validate(value, Object.assign(Object.assign({}, ((_b = (_a = allValues[TravellerFields.Documents]) === null || _a === void 0 ? void 0 : _a[index]) !== null && _b !== void 0 ? _b : {})), { nationality: (_d = (_c = formState.values[TravellerFields.Documents]) === null || _c === void 0 ? void 0 : _c[index]) === null || _d === void 0 ? void 0 : _d[TravellerDocumentFields.Nationality] }), InputType.Date, validation === null || validation === void 0 ? void 0 : validation.validationRules);
    };
    const buildSaveHandler = (onSave) => () => {
        var _a, _b;
        const fields = documentFields;
        if (!isDocExpireDateHidden) {
            fields.push(TravellerDocumentFields.DocExpiryDate);
        }
        fields.forEach(field => form.blur(`documents[${index}].${field}`));
        const docErrors = (_b = (_a = form.getState().errors) === null || _a === void 0 ? void 0 : _a[TravellerFields.Documents]) === null || _b === void 0 ? void 0 : _b[index];
        const hasErrors = fields.some(field => !!(docErrors === null || docErrors === void 0 ? void 0 : docErrors[field]));
        if (!hasErrors) {
            onSave();
        }
    };
    // Citizenships
    const sortedCitizenships = useMemo(() => {
        return [...citizenships].sort((a, b) => b.priority - a.priority);
    }, [citizenships]);
    const citizenshipsOptions = useMemo(() => {
        return sortedCitizenships.map(option => ({
            label: tPassenger(option.label),
            value: option.value
        }));
    }, [sortedCitizenships, tPassenger]);
    const citizenshipsByValue = useMemo(() => {
        return citizenshipsOptions.reduce((acc, curr) => {
            acc[curr.value] = curr;
            return acc;
        }, {});
    }, [citizenshipsOptions]);
    // Document Types
    const documentTypesOptions = useMemo(() => {
        return documentTypes.map(option => ({
            label: tPassenger(option),
            value: option
        }));
    }, [documentTypes, tPassenger]);
    const documentTypesByValue = useMemo(() => {
        return documentTypesOptions.reduce((acc, curr) => {
            acc[curr.value] = curr;
            return acc;
        }, {});
    }, [documentTypesOptions]);
    return {
        form,
        citizenshipsByValue,
        citizenshipsOptions,
        documentTypesOptions,
        documentTypesByValue,
        isDocExpireDateHidden,
        isBirthRegDocument,
        documentFields,
        citizenshipValidate,
        docTypeValidate,
        docNumberValidate,
        docExpireDateValidate,
        buildSaveHandler
    };
};
